import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Loader2, ShieldAlert } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { api } from '../../lib/api';

const STATUS_LABELS = { needs_review: 'Needs Review', source_based: 'Source-Based', verified: 'Verified', in_progress: 'In Progress', resolved: 'Resolved' };
const AMOUNT_LABELS = { reported: 'Reported', estimated: 'Estimated', source_needed: 'Source Needed', source_based: 'Source-Based', verified: 'Verified' };

const EMPTY = {
  title: '', category: 'other', amount: '', amount_label: 'source_needed', status: 'needs_review',
  source_status: 'source_needed', source_url: '', source_name: '', summary: '', why_it_matters: '',
  question: '', related_chart_id: '', related_article_slug: '', related_meeting_id: '',
  related_issue_category: '', published: false, order: 0,
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

export default function AdminSavings() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item object or EMPTY clone
  const [busy, setBusy] = useState(false);
  const [charts, setCharts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [issueCats, setIssueCats] = useState([]);
  const [settings, setSettings] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/savings').then((r) => { setItems(r.data.items || []); setCategories(r.data.categories || {}); }).catch(() => toast.error('Could not load savings items')).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get('/admin/savings-settings').then((r) => setSettings(r.data)).catch(() => {});
    api.get('/datasets').then((r) => setCharts((Array.isArray(r.data) ? r.data : r.data?.datasets || []))).catch(() => {});
    api.get('/news').then((r) => setArticles(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/meetings').then((r) => setMeetings(Array.isArray(r.data) ? r.data : r.data?.meetings || [])).catch(() => {});
    api.get('/admin/issue-categories').then((r) => setIssueCats(r.data.categories || [])).catch(() => {});
  }, [load]);

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const save = () => {
    if (!editing.title.trim()) { toast.error('Title is required'); return; }
    setBusy(true);
    const req = editing.id ? api.put(`/admin/savings/${editing.id}`, editing) : api.post('/admin/savings', editing);
    req.then(() => { toast.success('Savings item saved'); setEditing(null); load(); })
      .catch((err) => toast.error(err.response?.data?.detail || 'Could not save item'))
      .finally(() => setBusy(false));
  };

  const remove = (item) => {
    if (!window.confirm(`Delete \"${item.title}\"?`)) return;
    api.delete(`/admin/savings/${item.id}`).then(() => { toast.success('Deleted'); load(); }).catch(() => toast.error('Could not delete'));
  };

  const saveSettings = () => {
    api.put('/admin/savings-settings', settings).then((r) => { setSettings(r.data); toast.success('Savings Tracker settings saved'); }).catch(() => toast.error('Could not save settings'));
  };

  return (
    <div data-testid="admin-savings-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">Savings Tracker</h1>
        <Button onClick={() => setEditing({ ...EMPTY })} className="h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="savings-add-button"><Plus className="h-4 w-4 mr-1.5" /> Add Item</Button>
      </div>
      <p className="text-sm text-slate-500 mb-4">Waste, savings opportunities, costly systems, and taxpayer questions Steve has flagged. Never publish unverified dollar amounts as fact — label every figure.</p>

      <Card className="mb-4 border-amber-200 bg-amber-50/60"><CardContent className="p-4 flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-900 leading-relaxed">Truthfulness guard: an amount can only be labelled <b>Verified</b> or <b>Source-Based</b> when a source link/document is attached. The Launch Audit flags published items that break this rule.</p>
      </CardContent></Card>

      {settings && (
        <Card className="mb-5"><CardContent className="p-4 space-y-3" data-testid="savings-settings-card">
          <p className="font-bold text-[#1a2744] text-sm">Page text</p>
          <Field label="Tagline"><Input value={settings.tagline} onChange={(e) => setSettings((s) => ({ ...s, tagline: e.target.value }))} className="h-11" data-testid="savings-settings-tagline" /></Field>
          <Field label="Intro"><Textarea value={settings.intro} onChange={(e) => setSettings((s) => ({ ...s, intro: e.target.value }))} rows={2} data-testid="savings-settings-intro" /></Field>
          <Field label="Disclaimer"><Textarea value={settings.disclaimer} onChange={(e) => setSettings((s) => ({ ...s, disclaimer: e.target.value }))} rows={2} data-testid="savings-settings-disclaimer" /></Field>
          <Button onClick={saveSettings} variant="outline" className="h-10 font-bold btn-press" data-testid="savings-settings-save">Save Page Text</Button>
        </CardContent></Card>
      )}

      {loading ? <p className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p> : (
        <div className="space-y-3">
          {items.length === 0 && <Card><CardContent className="p-5 text-sm text-slate-500">No savings items yet. Add the first flagged item.</CardContent></Card>}
          {items.map((item) => (
            <Card key={item.id} data-testid="admin-savings-item">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <p className="font-bold text-[#1a2744] truncate">{item.title}</p>
                    <Badge variant="outline" className="text-[10px] font-bold">{categories[item.category] || item.category}</Badge>
                    <Badge variant="outline" className="text-[10px] font-bold">{STATUS_LABELS[item.status]}</Badge>
                    {item.amount && <Badge variant="outline" className="text-[10px] font-bold bg-slate-50">{item.amount} · {AMOUNT_LABELS[item.amount_label]}</Badge>}
                    <Badge variant="outline" className={`text-[10px] font-bold ${item.published ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-slate-100 text-slate-500'}`}>{item.published ? 'Published' : 'Draft'}</Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => setEditing({ ...EMPTY, ...item })} variant="outline" size="sm" className="h-9 font-bold btn-press" data-testid="savings-edit-button"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                    <Button onClick={() => remove(item)} variant="outline" size="sm" className="h-9 text-red-600 border-red-200 font-bold btn-press" data-testid="savings-delete-button"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="savings-edit-dialog">
          <DialogHeader><DialogTitle className="font-serif">{editing?.id ? 'Edit Savings Item' : 'Add Savings Item'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Field label="Title"><Input value={editing.title} onChange={(e) => set('title', e.target.value)} className="h-11" data-testid="savings-form-title" /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Category">
                  <Select value={editing.category} onValueChange={(v) => set('category', v)}>
                    <SelectTrigger className="h-11" data-testid="savings-form-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(categories).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Status">
                  <Select value={editing.status} onValueChange={(v) => set('status', v)}>
                    <SelectTrigger className="h-11" data-testid="savings-form-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Estimated cost / savings (optional)"><Input value={editing.amount} onChange={(e) => set('amount', e.target.value)} placeholder='e.g. $700,000/year' className="h-11" data-testid="savings-form-amount" /></Field>
                <Field label="Amount label (required with amount)">
                  <Select value={editing.amount_label} onValueChange={(v) => set('amount_label', v)}>
                    <SelectTrigger className="h-11" data-testid="savings-form-amount-label"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(AMOUNT_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Source link / document URL"><Input value={editing.source_url} onChange={(e) => set('source_url', e.target.value)} placeholder="https://…" className="h-11" data-testid="savings-form-source-url" /></Field>
                <Field label="Source name"><Input value={editing.source_name} onChange={(e) => set('source_name', e.target.value)} placeholder="e.g. 2025 CKL Budget, p. 42" className="h-11" data-testid="savings-form-source-name" /></Field>
              </div>
              <Field label="Summary"><Textarea value={editing.summary} onChange={(e) => set('summary', e.target.value)} rows={3} data-testid="savings-form-summary" /></Field>
              <Field label="Why it matters"><Textarea value={editing.why_it_matters} onChange={(e) => set('why_it_matters', e.target.value)} rows={3} data-testid="savings-form-why" /></Field>
              <Field label="The question Steve is asking"><Textarea value={editing.question} onChange={(e) => set('question', e.target.value)} rows={2} data-testid="savings-form-question" /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Related City Data chart">
                  <Select value={editing.related_chart_id || 'none'} onValueChange={(v) => set('related_chart_id', v === 'none' ? '' : v)}>
                    <SelectTrigger className="h-11" data-testid="savings-form-chart"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem>{charts.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Related Newsroom article">
                  <Select value={editing.related_article_slug || 'none'} onValueChange={(v) => set('related_article_slug', v === 'none' ? '' : v)}>
                    <SelectTrigger className="h-11" data-testid="savings-form-article"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem>{articles.map((a) => <SelectItem key={a.slug} value={a.slug}>{a.title}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Related Council meeting">
                  <Select value={editing.related_meeting_id || 'none'} onValueChange={(v) => set('related_meeting_id', v === 'none' ? '' : v)}>
                    <SelectTrigger className="h-11" data-testid="savings-form-meeting"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem>{meetings.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Related Issue Tracker category">
                  <Select value={editing.related_issue_category || 'none'} onValueChange={(v) => set('related_issue_category', v === 'none' ? '' : v)}>
                    <SelectTrigger className="h-11" data-testid="savings-form-issue-category"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem>{issueCats.map((c) => <SelectItem key={c.value || c} value={c.value || c}>{c.label || c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">Published <Switch checked={editing.published} onCheckedChange={(v) => set('published', v)} data-testid="savings-form-published" /></span>
                <div className="flex gap-2">
                  <Button onClick={() => setEditing(null)} variant="outline" className="h-11 font-bold btn-press">Cancel</Button>
                  <Button onClick={save} disabled={busy} className="h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="savings-form-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Item'}</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
